import { useId } from "react";

/**
 * Accessible form field: associates a visible <label>, optional instructions,
 * and an error message with the control via htmlFor / aria-describedby /
 * aria-invalid / aria-required. Errors are announced (role="alert") and never
 * communicated by color alone (an icon + text prefix is used).
 *
 * as: "input" | "textarea" | "select"
 */
export const AccessibleField = ({
  label,
  as = "input",
  required = false,
  error = "",
  hint = "",
  className = "",
  inputClassName = "",
  children, // for <select> options
  ...control
}) => {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(" ") || undefined;

  const base =
    "w-full rounded-xl border bg-white px-4 py-3 text-[15px] text-[#0a1128] placeholder:text-slate-500 outline-none transition-[border-color,box-shadow] " +
    (error ? "border-[#b00020] focus:ring-2 focus:ring-[#b00020]/30" : "border-slate-300 focus:border-[#d90429] focus:ring-2 focus:ring-[#d90429]/20") +
    (inputClassName ? ` ${inputClassName}` : "");

  const shared = {
    id,
    "aria-required": required || undefined,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy,
    className: base,
    ...control,
  };

  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium text-[#0b192c] mb-1.5">
        {label}
        {required && <span className="text-[#b00020]"> *<span className="sr-only"> required</span></span>}
      </label>
      {hint && <p id={hintId} className="text-xs text-slate-500 mb-1.5">{hint}</p>}
      {as === "textarea" ? (
        <textarea {...shared} />
      ) : as === "select" ? (
        <select {...shared}>{children}</select>
      ) : (
        <input {...shared} />
      )}
      {error && (
        <p id={errId} role="alert" className="mt-1.5 text-sm font-medium text-[#b00020] flex items-start gap-1.5">
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </p>
      )}
    </div>
  );
};
